import { IEstabelecimentoOrganizacao } from "../../interfaces/IOrganizacao";

// A apresentação da situação é compartilhada com a tela de seleção de
// estabelecimentos, então vive na feature estabelecimento.
export { apresentarSituacao, rotuloAssinatura } from "../../../estabelecimento/utils/situacaoAcesso";
export type { IApresentacaoSituacao } from "../../../estabelecimento/utils/situacaoAcesso";

export const formatarEndereco = (endereco?: { rua?: string; numero?: string; bairro?: string; cidade?: string; uf?: string }) => {
    if (!endereco?.rua) {
        return null;
    }

    const linha = [endereco.rua, endereco.numero].filter(Boolean).join(", ");
    const complemento = [endereco.bairro, endereco.cidade && `${endereco.cidade}${endereco.uf ? `/${endereco.uf}` : ""}`]
        .filter(Boolean)
        .join(" - ");

    return [linha, complemento].filter(Boolean).join(" · ");
};

export const nomeEstabelecimento = (estabelecimento: { nomeFantasia?: string; razaoSocial?: string; nomeCompleto?: string }) =>
    estabelecimento.nomeFantasia || estabelecimento.razaoSocial || estabelecimento.nomeCompleto || "Sem nome";

export const documentoEstabelecimento = (estabelecimento: { cnpj?: string; cpf?: string }) => {
    const cnpj = estabelecimento.cnpj;
    if (cnpj) {
        // CNPJ alfanumérico: a máscara é posicional, não numérica.
        return cnpj.length === 14
            ? `${cnpj.slice(0, 2)}.${cnpj.slice(2, 5)}.${cnpj.slice(5, 8)}/${cnpj.slice(8, 12)}-${cnpj.slice(12)}`
            : cnpj;
    }

    const cpf = estabelecimento.cpf;
    if (cpf?.length === 11) {
        return `${cpf.slice(0, 3)}.${cpf.slice(3, 6)}.${cpf.slice(6, 9)}-${cpf.slice(9)}`;
    }

    return cpf ?? null;
};

export const iniciais = (nome?: string) =>
    (nome ?? "")
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map(parte => parte.charAt(0).toUpperCase())
        .join("") || "?";

/** O `<input type="date">` só aceita YYYY-MM-DD; o corte evita converter e escorregar de fuso. */
export const paraInputData = (data?: string | null) => (data ? String(data).slice(0, 10) : "");

export interface IResumoOrganizacao {
    total: number;
    /** Estabelecimentos em que se consegue entrar hoje. */
    ativos: number;
    bloqueados: number;
    assinaturasAtivas: number;
    emTeste: number;
    canceladas: number;
    semAssinatura: number;
    /** Soma das assinaturas ativas que já saíram do período de teste. */
    receitaMensal: number;
}

/**
 * Indicadores da organização. O período de teste sai da receita — ele ainda não
 * é dinheiro —, na mesma conta que o backoffice faz para o MRR da plataforma.
 */
export const resumirOrganizacao = (estabelecimentos: IEstabelecimentoOrganizacao[]): IResumoOrganizacao =>
    estabelecimentos.reduce<IResumoOrganizacao>((resumo, estabelecimento) => {
        const situacao = estabelecimento.situacao;
        resumo.total += 1;

        if (situacao?.bloqueado) {
            resumo.bloqueados += 1;
        } else {
            resumo.ativos += 1;
        }

        if (situacao?.situacaoAssinatura === "SEM_ASSINATURA") {
            resumo.semAssinatura += 1;
        }

        if (situacao?.situacaoAssinatura === "CANCELADA") {
            resumo.canceladas += 1;
        }

        if (situacao?.situacaoAssinatura === "ATIVA") {
            resumo.assinaturasAtivas += 1;

            if (situacao.freeTier) {
                resumo.emTeste += 1;
            } else {
                resumo.receitaMensal += situacao.valorAssinatura ?? 0;
            }
        }

        return resumo;
    }, { total: 0, ativos: 0, bloqueados: 0, assinaturasAtivas: 0, emTeste: 0, canceladas: 0, semAssinatura: 0, receitaMensal: 0 });
